import type { Company, CompanyImage, GalleryItem } from "@/modules/companies/client/api/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import { useState } from "react"

import { Text } from "@jobteaser/spark/components/Text"
import IconCamera from "@jobteaser/ui-kit/assets/icons/jds_camera.svg"
import { Icon, ICON_SIZES } from "@jobteaser/ui-kit/dist/module/components/Icon/Icon"

import { MediaImage } from "@/modules/companies/client/components/Media/Image"
import { MEDIA_TYPE_IMAGE } from "@/modules/companies/client/constants/enums"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./ImagesGallery.module.css"

const ImagesModalDynamic = dynamic(() =>
  import("@/modules/companies/client/components/MediaGallery/ImagesModal").then(mod => mod.ImagesModal)
)

type ImagesGalleryProps = {
  className?: string
  companyGallery: Company["page"]["gallery"]
  companyId: Company["id"]
  companyName: Company["page"]["name"]
  imageType?: string
  withTabletBreakpoint?: boolean
}

export const ImagesGallery: FunctionComponent<ImagesGalleryProps> = ({
  className,
  companyId,
  companyName,
  companyGallery,
  imageType = MEDIA_TYPE_IMAGE,
  withTabletBreakpoint = true,
}) => {
  const tracking = useTracking()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [initialIndex, setInitialIndex] = useState<number>(0)

  const galleryImages: CompanyImage[] =
    (companyGallery.filter(media => media.type === imageType) as GalleryItem<"MEDIA_TYPE_IMAGE">[]) || []

  if (galleryImages.length < 1) return null

  const displayModal = (index: number): void => {
    tracking?.track("media_image_click", {
      company_id: companyId,
      company_name: companyName,
      context: "images_gallery",
      service: "company",
    })
    setShowModal(true)
    setInitialIndex(index)
  }

  const galleryClassNames = clsx(
    styles.gallery,
    { [styles["gallery--oneImage"]]: galleryImages.length === 1 },
    { [styles["gallery--twoImages"]]: galleryImages.length === 2 },
    { [styles["gallery--threeImages"]]: galleryImages.length === 3 },
    { [styles["gallery--moreImages"]]: galleryImages.length > 3 },
    { [styles["gallery--withTabletBreakpoint"]]: withTabletBreakpoint },
    className
  )

  return (
    <div className={galleryClassNames} data-testid="company_media_gallery_list">
      {galleryImages.slice(0, 3)?.map((currentMedia, index) => {
        const { description, url } = currentMedia
        const showOverlayOnFirstImage = index === 0
        const showOverlayOnLastImage = galleryImages.length > 3 && index === 2
        const showOverlay = galleryImages.length > 1 && (showOverlayOnFirstImage || showOverlayOnLastImage)

        return (
          <button
            key={url}
            type="button"
            className={clsx(styles.thumbnail, showOverlay && styles["thumbnail--withOverlay"])}
            onClick={() => displayModal(index)}
          >
            <MediaImage
              wrapperClassName={styles.imageWrapper}
              alt={description || ""}
              src={url}
              priority={index === 0}
              sizes={
                index === 0
                  ? "(max-width: 30rem) 100vw, (max-width: 48rem) 66vw, 50vw"
                  : "(max-width: 48rem) 33vw, 25vw"
              }
            />
            {showOverlay && (
              <div className={styles.overlay}>
                <div
                  className={clsx(
                    styles.show,
                    showOverlayOnFirstImage && styles["show--onFirstImage"],
                    showOverlayOnLastImage && styles["show--onLastImage"]
                  )}
                >
                  <Icon xlinkHref={`#${IconCamera.id}`} size={ICON_SIZES.MEDIUM} role="graphics-symbol" />
                  <Text variant="body2" weight="semiBold">
                    {galleryImages.length}
                  </Text>
                </div>
              </div>
            )}
          </button>
        )
      })}
      {showModal && (
        <ImagesModalDynamic gallery={galleryImages} initialIndex={initialIndex} onClose={() => setShowModal(false)} />
      )}
    </div>
  )
}
